import React, {
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { LayoutChangeEvent, ViewStyle } from 'react-native';
import { Dimensions, StyleSheet, TouchableOpacity, View, type ViewInstance } from 'react-native';
import Animated, {
  Easing,
  type EntryOrExitLayoutType,
  Keyframe,
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { scheduleOnRN } from 'react-native-worklets';

import { theme } from '@garnish/constants';
import { useCallbackIfMounted } from '@garnish/core';
import { usePressableState } from '@garnish/core';
import { BodyText } from '@garnish/core';
import { nanoid } from 'nanoid/non-secure';

import { AutomaticallyPlacedView, useAutomaticPlacement } from '../../hooks/useAutomaticPlacement';
import { Overlay } from '../Overlay';
import { checkIfHasPointerDevice } from './WithTooltip.utils';

import type { TooltipPlacement, TooltipProps, WithTooltipProps } from './WithTooltip.types';

// ────────────────────────────────────────────────────────────────────────────────

export const WithTooltip = ({
  children,
  text,
  triggerMode = 'press',
  placement = 'top',
  toggleAccessibilityLabel = 'Toggle tooltip',
  testID = 'sg-with-tooltip-toggle',
  offsetX = 0,
  offsetY = 0,
  autoDismissTimeout,
  onShow,
}: WithTooltipProps) => {
  const tooltipNativeID = useMemo(() => `tooltip-${nanoid()}`, []);
  const shouldShowTooltipOnHover = checkIfHasPointerDevice() && triggerMode === 'hover';

  const toggleRef = useRef(null);
  const tooltipRef = useRef(null);

  const togglePressableState = usePressableState(toggleRef);

  const [shouldShowTooltip, setShouldShowTooltip] = useState(false);
  const [currentPlacement, setCurrentPlacement] = useState(placement);
  const [isTooltipPlaced, setIsTooltipPlaced] = useState(false);
  const [isOverlayShown, setIsOverlayShown] = useState(false);

  const shouldShowTooltipRef = useRef(shouldShowTooltip);

  shouldShowTooltipRef.current = shouldShowTooltip;

  const isTooltipPlacedRef = useRef(isTooltipPlaced);

  isTooltipPlacedRef.current = isTooltipPlaced;

  //
  // ─── HELPERS ──────────────────────────────────────────────────────────────────
  //

  // Reference to clear timeout in case the component unmounts.
  const autoDismissTimeoutRef = useRef<ReturnType<typeof setTimeout>>(null);
  const dismissTooltip = useCallbackIfMounted(() => {
    setShouldShowTooltip(false);
  });

  const toggleTooltip = useCallback(() => {
    setShouldShowTooltip((current) => !current);

    // Automatically dismiss tooltip after some time.
    if (autoDismissTimeout) {
      autoDismissTimeoutRef.current = setTimeout(dismissTooltip, autoDismissTimeout);
    }
  }, [autoDismissTimeout, dismissTooltip]);

  // Clear possible timeout if the component unmounts.
  useEffect(() => {
    return () => {
      if (autoDismissTimeoutRef.current) {
        clearTimeout(autoDismissTimeoutRef.current);
      }
    };
  }, []);

  // Keeps the overlay (a modal on native) open until the tooltip has faded out.
  const handleTooltipHidden = useCallback(() => {
    if (shouldShowTooltipRef.current) {
      return;
    }

    setIsOverlayShown(false);
  }, []);

  // Late measurements of a closed tooltip must not mark the next opening as placed.
  const handleTooltipPlaced = useCallback(() => {
    if (!shouldShowTooltipRef.current) {
      return;
    }

    setIsTooltipPlaced(true);
  }, []);

  // automatic tooltip placement
  const placementX = useSharedValue(0);
  const placementY = useSharedValue(0);
  const {
    updatePlacement: updateTooltipLocation,
    updatePlacementDebounced: updateTooltipLocationDebounced,
  } = useAutomaticPlacement({
    placementX,
    placementY,
    triggerRef: toggleRef as any,
    contentRef: tooltipRef as any,
    placement,
    setCurrentPlacement,
    onPlaced: handleTooltipPlaced,
  });

  // Closed tooltips skip measuring, they are placed when opened.
  const handleToggleLayout = useCallback(() => {
    if (!shouldShowTooltipRef.current) {
      return;
    }

    updateTooltipLocation();
  }, [updateTooltipLocation]);

  // Only an open tooltip follows window resizes, so closed ones never re-render on resize.
  const followWindowResizes = useCallback(() => {
    if (!shouldShowTooltip) {
      return;
    }

    const subscription = Dimensions.addEventListener('change', updateTooltipLocationDebounced);

    return () => {
      subscription.remove();
    };
  }, [shouldShowTooltip, updateTooltipLocationDebounced]);

  //
  // ─── EFFECTS ──────────────────────────────────────────────────────────────────
  //

  // sync the tooltip's placement if it has been changed externally
  useEffect(() => {
    setCurrentPlacement(placement);

    if (!shouldShowTooltipRef.current) {
      return;
    }

    updateTooltipLocation();
  }, [placement, updateTooltipLocation]);

  useLayoutEffect(() => {
    if (!shouldShowTooltip) return;
    updateTooltipLocationDebounced();
  }, [updateTooltipLocationDebounced, shouldShowTooltip]);

  // A closed tooltip is re-measured on its next opening, so it can reveal in the right place.
  useEffect(() => {
    if (shouldShowTooltip) {
      setIsOverlayShown(true);

      return;
    }

    // A tooltip closed before it was placed never entered, so there is no exit to wait for.
    if (!isTooltipPlacedRef.current) {
      setIsOverlayShown(false);
    }

    setIsTooltipPlaced(false);
  }, [shouldShowTooltip]);

  useEffect(followWindowResizes, [followWindowResizes]);

  // show/hide tooltip on hover/focus (web)
  useEffect(() => {
    if (!shouldShowTooltipOnHover) return;
    setShouldShowTooltip(togglePressableState.isHovered);
  }, [togglePressableState.isHovered, shouldShowTooltipOnHover]);

  useEffect(() => {
    if (shouldShowTooltip) onShow?.();
  }, [onShow, shouldShowTooltip]);

  // ──────────────────────────────────────────────────────────────────────────────

  return (
    <>
      {/* ========== toggle ========== */}
      <TouchableOpacity
        ref={toggleRef}
        style={styles.toggle}
        testID={testID}
        onLayout={handleToggleLayout}
        onPress={toggleTooltip}
        accessibilityRole="button"
        accessibilityLabel={toggleAccessibilityLabel}
        // @ts-expect-error TS(2322): Type '{ children: ReactNode; ref: RefObject<null>;... Remove this comment to see the full error message
        accessibilityDescribedBy={shouldShowTooltip ? tooltipNativeID : undefined}
      >
        {children}
      </TouchableOpacity>

      {/* ========== tooltip ========== */}
      <Overlay
        triggerRef={toggleRef as any}
        contentRef={tooltipRef as any}
        dismiss={dismissTooltip}
        show={shouldShowTooltip || isOverlayShown}
      >
        <AutomaticallyPlacedView
          placementX={placementX}
          placementY={placementY}
          offsetX={offsetX}
          offsetY={offsetY}
          style={styles.tooltipWrapper}
        >
          <Tooltip
            ref={tooltipRef}
            text={text}
            placement={currentPlacement}
            onLayout={updateTooltipLocationDebounced}
            nativeID={tooltipNativeID}
            shouldShowTooltip={shouldShowTooltip}
            isPlaced={isTooltipPlaced}
            onHidden={handleTooltipHidden}
          />
        </AutomaticallyPlacedView>
      </Overlay>
    </>
  );
};

//
// ─── SUB-COMPONENTS ─────────────────────────────────────────────────────────────
//

export const Tooltip = React.memo(
  React.forwardRef<ViewInstance, TooltipProps>((props, ref) => {
    const {
      shouldShowTooltip,
      isPlaced = true,
      placement,
      text,
      nativeID,
      onLayout,
      onHidden,
    } = props;

    const handleExitFinished = useCallback(
      (isFinished: boolean) => {
        'worklet';

        if (!isFinished || !onHidden) {
          return;
        }

        scheduleOnRN(onHidden);
      },
      [onHidden],
    );

    const exitingAnimation = useMemo(
      () =>
        new Keyframe(EXITING_KEYFRAMES[placement])
          .duration(REVEAL_DURATION)
          .withCallback(handleExitFinished),
      [handleExitFinished, placement],
    );

    if (!shouldShowTooltip) {
      return null;
    }

    return (
      <TooltipReveal isPlaced={isPlaced} placement={placement} exiting={exitingAnimation}>
        <TooltipContent
          ref={ref}
          text={text}
          nativeID={nativeID}
          placement={placement}
          shouldShowTooltip={shouldShowTooltip}
          onLayout={onLayout}
        />
      </TooltipReveal>
    );
  }),
);

/**
 * Mounts once per opening, so every opening starts invisible. It waits for the
 * tooltip to be placed before fading in, then `exiting` plays when it unmounts.
 */
const TooltipReveal = (props: TooltipRevealProps) => {
  const { isPlaced, placement, exiting, children } = props;

  const revealProgress = useSharedValue(0);

  // ─── Effects ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!isPlaced) {
      return;
    }

    revealProgress.value = withTiming(1, REVEAL_TIMING_CONFIG);
  }, [isPlaced, revealProgress]);

  // ─── Styles ────────────────────────────────────────────────────────────────

  const revealStyle = useAnimatedStyle(() => {
    const distance = (1 - revealProgress.value) * REVEAL_DISTANCE;

    return {
      opacity: revealProgress.value,
      transform: getRevealTransform(placement, distance),
    };
  }, [placement]);

  // On web, `exiting` replays from this view's position as of its last render, so the reveal
  // only moves the inner view and the outer one stays where the tooltip ends up.
  return (
    <Animated.View exiting={exiting} pointerEvents={isPlaced ? 'auto' : 'none'}>
      <Animated.View style={revealStyle}>{children}</Animated.View>
    </Animated.View>
  );
};

const TooltipContent = (props: TooltipContentProps) => {
  const { ref, shouldShowTooltip, placement, text, nativeID, onLayout } = props;

  return (
    <View
      style={[styles.tooltipWrapperInner, TooltipWrapperLayoutStyles[placement]]}
      onLayout={onLayout}
      ref={ref}
    >
      <View style={[styles.tooltipElement, styles.tooltipTextWrapper]}>
        <BodyText
          sizeMatch={['14']}
          nativeID={nativeID}
          // @ts-expect-error TS(2322): Type '{ children: string; size: number; nativeID: ... Remove this comment to see the full error message
          accessibilityHidden={!shouldShowTooltip}
          testID="sg-with-tooltip-text"
        >
          {text}
        </BodyText>
      </View>

      <TooltipArrow placement={placement} />
    </View>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

export const TooltipArrow = ({ placement }: Required<Pick<WithTooltipProps, 'placement'>>) => {
  const layoutStyles = ArrowLayoutStyles[placement];

  return (
    <View
      testID="sg-tooltip-arrow"
      style={[styles.tooltipElement, styles.arrow, layoutStyles]}
      pointerEvents="none"
    />
  );
};

//
// ─── CONSTANTS ──────────────────────────────────────────────────────────────────
//

const TOOLTIP_OFFSET = 10;

const REVEAL_DISTANCE = 4;
const REVEAL_DURATION = theme.transitions.short;
const REVEAL_TIMING_CONFIG = { duration: REVEAL_DURATION, easing: Easing.out(Easing.quad) };
const TOOLTIP_CONTENT_MAX_WIDTH = 180;

const TOOLTIP_ARROW_SIZE = 14;
const TOOLTIP_ARROW_SHADOW_OFFSET = 3;

// negative offset is used to make the rectangle look like a triangle / arrow
export const TOOLTIP_ARROW_OFFSET = -(TOOLTIP_ARROW_SIZE / 2 + 2);

//
// ─── STYLES ─────────────────────────────────────────────────────────────────────
//

const styles = StyleSheet.create({
  toggle: {
    flexShrink: 1,
  },
  tooltipWrapper: {
    top: 0,
    left: 0,
    zIndex: theme.zIndex.tooltip,
  },
  tooltipWrapperInner: {
    alignItems: 'center',
    padding: TOOLTIP_OFFSET,
  },
  tooltipWrapperTopPlacement: {
    flexDirection: 'column',
  },
  tooltipWrapperRightPlacement: {
    flexDirection: 'row-reverse',
  },
  tooltipWrapperBottomPlacement: {
    flexDirection: 'column-reverse',
  },
  tooltipWrapperLeftPlacement: {
    flexDirection: 'row',
  },
  tooltipElement: {
    position: 'relative',
    backgroundColor: theme.colors.NEUTRAL_7,
    borderRadius: theme.radius.small,
  },
  tooltipTextWrapper: {
    color: theme.colors.GREEN_1,
    paddingVertical: theme.spacing['2'],
    paddingHorizontal: theme.spacing['3'],
    maxWidth: TOOLTIP_CONTENT_MAX_WIDTH,
    ...theme.elevations['4'],
  },

  //
  // ─── ARROW ────────────────────────────────────────────────────────────────────
  //

  arrow: {
    width: TOOLTIP_ARROW_SIZE,
    height: TOOLTIP_ARROW_SIZE,
    flexShrink: 0,
    transform: [{ rotate: '45deg' }],
    borderRadius: 2,
    ...theme.elevations['4'],
  },
  arrowTopPlacement: {
    marginTop: TOOLTIP_ARROW_OFFSET,
    boxShadow: [
      {
        offsetX: TOOLTIP_ARROW_SHADOW_OFFSET,
        offsetY: TOOLTIP_ARROW_SHADOW_OFFSET,
        blurRadius: 2.62,
        color: 'rgba(0, 0, 0, 0.15)',
      },
    ],
  },
  arrowRightPlacement: {
    marginRight: TOOLTIP_ARROW_OFFSET,
    boxShadow: [
      {
        offsetX: -TOOLTIP_ARROW_SHADOW_OFFSET,
        offsetY: TOOLTIP_ARROW_SHADOW_OFFSET,
        blurRadius: 2.62,
        color: 'rgba(0, 0, 0, 0.06)',
      },
    ],
  },
  arrowBottomPlacement: {
    marginBottom: TOOLTIP_ARROW_OFFSET,
    boxShadow: [
      {
        offsetX: -TOOLTIP_ARROW_SHADOW_OFFSET,
        offsetY: -TOOLTIP_ARROW_SHADOW_OFFSET,
        blurRadius: 2.62,
        color: 'rgba(0, 0, 0, 0.03)',
      },
    ],
  },
  arrowLeftPlacement: {
    marginLeft: TOOLTIP_ARROW_OFFSET,
    boxShadow: [
      {
        offsetX: TOOLTIP_ARROW_SHADOW_OFFSET,
        offsetY: -TOOLTIP_ARROW_SHADOW_OFFSET,
        blurRadius: 2.62,
        color: 'rgba(0, 0, 0, 0.06)',
      },
    ],
  },
});

const TooltipWrapperLayoutStyles: Record<TooltipPlacement, ViewStyle> = {
  top: styles.tooltipWrapperTopPlacement,
  right: styles.tooltipWrapperRightPlacement,
  bottom: styles.tooltipWrapperBottomPlacement,
  left: styles.tooltipWrapperLeftPlacement,
};

const ArrowLayoutStyles: Record<TooltipPlacement, ViewStyle> = {
  top: styles.arrowTopPlacement,
  right: styles.arrowRightPlacement,
  bottom: styles.arrowBottomPlacement,
  left: styles.arrowLeftPlacement,
};

/**
 * Mirrors the reveal, so each tooltip fades out while sliding back into its trigger.
 */
const EXITING_KEYFRAMES = {
  top: createExitingKeyframes({ translateY: 0 }, { translateY: REVEAL_DISTANCE }),
  right: createExitingKeyframes({ translateX: 0 }, { translateX: -REVEAL_DISTANCE }),
  bottom: createExitingKeyframes({ translateY: 0 }, { translateY: -REVEAL_DISTANCE }),
  left: createExitingKeyframes({ translateX: 0 }, { translateX: REVEAL_DISTANCE }),
} satisfies Record<TooltipPlacement, ExitingKeyframes>;

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * Offsets the tooltip towards its trigger, so it slides out of it while fading in.
 */
function getRevealTransform(placement: TooltipPlacement, distance: number) {
  'worklet';

  if (placement === 'top') {
    return [{ translateY: distance }];
  }

  if (placement === 'bottom') {
    return [{ translateY: -distance }];
  }

  if (placement === 'left') {
    return [{ translateX: distance }];
  }

  return [{ translateX: -distance }];
}

function createExitingKeyframes(shownOffset: RevealOffset, hiddenOffset: RevealOffset) {
  return {
    0: { opacity: 1, transform: [shownOffset] },
    100: { opacity: 0, transform: [hiddenOffset], easing: Easing.in(Easing.quad) },
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type TooltipContentProps = Pick<
  TooltipProps,
  'text' | 'nativeID' | 'placement' | 'shouldShowTooltip'
> & {
  ref?: Ref<ViewInstance>;
  onLayout?: (event: LayoutChangeEvent) => void;
};

type TooltipRevealProps = {
  isPlaced: boolean;
  placement: TooltipPlacement;
  exiting: EntryOrExitLayoutType;
  children: ReactNode;
};

type RevealOffset = { translateX: number } | { translateY: number };

type ExitingKeyframes = ReturnType<typeof createExitingKeyframes>;
